import { useEffect, useState } from "react";
import PropTypes from "prop-types";

import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";

import SidePanel from "components/SidePanel";
import AuthorForm from "components/content/AuthorForm";
import { listAuthors } from "api/endpoints/authors";

const NEW_AUTHOR = "__new_author__";

/**
 * Batch 2.3 — <AuthorSelect>. Picks a CMS author for the content. A
 * "+ New author" entry opens <AuthorForm> in a nested SidePanel; the created
 * author is appended to the list and auto-selected.
 */
function AuthorSelect({ value, onChange, websiteId, required }) {
  const [authors, setAuthors] = useState([]);
  const [panelOpen, setPanelOpen] = useState(false);

  const load = async () => {
    if (!websiteId) return;
    try {
      setAuthors(await listAuthors(websiteId));
    } catch {
      setAuthors([]);
    }
  };

  useEffect(() => {
    load();
  }, [websiteId]);

  const handleChange = (event) => {
    const next = event.target.value;
    if (next === NEW_AUTHOR) {
      setPanelOpen(true);
      return;
    }
    onChange(next);
  };

  return (
    <>
      <TextField
        select
        label="Author"
        value={value || ""}
        onChange={handleChange}
        fullWidth
        required={required}
      >
        <MenuItem value="" disabled>
          Select an author
        </MenuItem>
        {authors.map((author) => (
          <MenuItem key={author.author_id} value={author.author_id}>
            {author.display_name}
          </MenuItem>
        ))}
        <MenuItem value={NEW_AUTHOR}>+ New author</MenuItem>
      </TextField>

      <SidePanel
        open={panelOpen}
        onClose={() => setPanelOpen(false)}
        title="New Author"
        subtitle="Add an author without leaving this content"
        width={760}
      >
        {panelOpen && (
          <AuthorForm
            websiteId={websiteId}
            onSaved={async (created) => {
              setPanelOpen(false);
              await load();
              if (created?.author_id) onChange(created.author_id);
            }}
          />
        )}
      </SidePanel>
    </>
  );
}

AuthorSelect.defaultProps = {
  value: null,
  websiteId: undefined,
  required: false,
};

AuthorSelect.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  websiteId: PropTypes.string,
  required: PropTypes.bool,
};

export default AuthorSelect;
